import { lazy, Suspense, useEffect } from 'react';
import { BRAND } from './lib/brand';
import { clearStaleChunkGuard } from './lib/chunkReload';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { Provider } from 'react-redux';
import { store } from './store';
import { theme } from './theme';

// Eager: layouts, auth glue, and the highest-traffic entry points (landing,
// login, signup) where a lazy-load spinner would hurt first impressions.
import AppLayout from './components/layout/AppLayout';
import PublicLayout from './components/layout/PublicLayout';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import OAuthCallback from './components/auth/OAuthCallback';
import RequireAuth from './components/auth/RequireAuth';

// Lazy: everything else splits into per-route chunks so the public funnel
// pages don't pay for the app (and vice versa).
const Home = lazy(() => import('./pages/Home'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Policies = lazy(() => import('./pages/Policies'));
const Organization = lazy(() => import('./pages/Organization'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const Team = lazy(() => import('./pages/Team'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPostRoute = lazy(() => import('./pages/blog/BlogPostRoute'));
const Privacy = lazy(() => import('./pages/Privacy'));
const ToolsIndex = lazy(() => import('./pages/tools/ToolsIndex'));
const ToolPage = lazy(() => import('./pages/tools/ToolPage'));
const CompareIndex = lazy(() => import('./pages/compare/CompareIndex'));
const ComparePage = lazy(() => import('./pages/compare/ComparePage'));
const IncidentTracker = lazy(() => import('./pages/IncidentTracker'));
const ThreatTracker = lazy(() => import('./pages/ThreatTracker'));
const Terms = lazy(() => import('./pages/Terms'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Compliance = lazy(() => import('./pages/Compliance'));
const Assistant = lazy(() => import('./pages/Assistant'));
const QuestionnaireAssistant = lazy(() => import('./pages/QuestionnaireAssistant'));
const TrustPage = lazy(() => import('./pages/TrustPage'));
const RiskRegister = lazy(() => import('./pages/RiskRegister'));
const ThreatMatches = lazy(() => import('./pages/ThreatMatches'));
const InsuranceRenewal = lazy(() => import('./pages/InsuranceRenewal'));
const BoardReport = lazy(() => import('./pages/BoardReport'));
const PasswordReset = lazy(() => import('./pages/PasswordReset'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminShell = lazy(() => import('./pages/admin/AdminShell'));
const BlogAgent = lazy(() => import('./pages/admin/BlogAgent'));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <CircularProgress />
  </Box>
);

// Same Vercel deployment serves both hostnames - the admin subdomain (BRAND.adminUrl)
// gets an entirely separate, minimal route tree (own login, no customer-app
// sidebar) rather than sharing the main site's routes. The real access
// boundary doesn't move: requireAdmin on /api/admin/* is unaffected by any
// of this, it's purely presentational/routing separation.
const isAdminSubdomain = window.location.hostname.startsWith('admin.');

// Anyone with the old /admin link bookmarked lands here instead of a 404.
const AdminRedirect = () => {
  useEffect(() => {
    window.location.href = `${BRAND.adminUrl}${window.location.search}`;
  }, []);
  return <RouteFallback />;
};

function App() {
  // A successful render of the app root means this load's chunks are good -
  // clear the stale-chunk reload guard so a later, unrelated deploy still
  // gets its own one-time auto-reload instead of being silently skipped.
  useEffect(() => {
    clearStaleChunkGuard();
  }, []);

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Suspense fallback={<RouteFallback />}>
          {isAdminSubdomain ? (
            <Routes>
              <Route path="/login" element={<AdminLogin />} />
              <Route path="/forgot-password" element={<PasswordReset />} />
              <Route path="/reset-password" element={<PasswordReset />} />
              <Route path="/" element={<RequireAuth><AdminShell><Admin /></AdminShell></RequireAuth>} />
              <Route path="/blog" element={<RequireAuth><AdminShell><BlogAgent /></AdminShell></RequireAuth>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          ) : (
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<PublicLayout><Landing /></PublicLayout>} />
            <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
            <Route path="/signup" element={<PublicLayout><Signup /></PublicLayout>} />
            <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
            <Route path="/blog/:slug" element={<PublicLayout><BlogPostRoute /></PublicLayout>} />
            <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
            <Route path="/tools" element={<PublicLayout><ToolsIndex /></PublicLayout>} />
            <Route path="/tools/:slug" element={<PublicLayout><ToolPage /></PublicLayout>} />
            <Route path="/compare" element={<PublicLayout><CompareIndex /></PublicLayout>} />
            <Route path="/compare/:slug" element={<PublicLayout><ComparePage /></PublicLayout>} />
            <Route path="/incidents" element={<PublicLayout><IncidentTracker /></PublicLayout>} />
            <Route path="/threats" element={<PublicLayout><ThreatTracker /></PublicLayout>} />
            <Route path="/trust/:slug" element={<PublicLayout><TrustPage /></PublicLayout>} />
            <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
            <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />
            <Route path="/oauth/callback" element={<PublicLayout><OAuthCallback /></PublicLayout>} />
            <Route path="/accept-invite" element={<PublicLayout><AcceptInvite /></PublicLayout>} />
            <Route path="/forgot-password" element={<PublicLayout><PasswordReset /></PublicLayout>} />
            <Route path="/reset-password" element={<PublicLayout><PasswordReset /></PublicLayout>} />

            {/* Protected routes */}
            <Route path="/home" element={<RequireAuth><AppLayout><Home /></AppLayout></RequireAuth>} />
            <Route path="/dashboard" element={<RequireAuth><AppLayout><Dashboard /></AppLayout></RequireAuth>} />
            <Route path="/policies" element={<RequireAuth><AppLayout><Policies /></AppLayout></RequireAuth>} />
            <Route path="/organization" element={<RequireAuth><AppLayout><Organization /></AppLayout></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth><AppLayout><Profile /></AppLayout></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth><AppLayout><Settings /></AppLayout></RequireAuth>} />
            <Route path="/team" element={<RequireAuth><AppLayout><Team /></AppLayout></RequireAuth>} />
            <Route path="/compliance" element={<RequireAuth><AppLayout><Compliance /></AppLayout></RequireAuth>} />
            <Route path="/threat-matches" element={<RequireAuth><AppLayout><ThreatMatches /></AppLayout></RequireAuth>} />
            <Route path="/risks" element={<RequireAuth><AppLayout><RiskRegister /></AppLayout></RequireAuth>} />
            <Route path="/insurance-renewal" element={<RequireAuth><AppLayout><InsuranceRenewal /></AppLayout></RequireAuth>} />
            <Route path="/board-report" element={<RequireAuth><AppLayout><BoardReport /></AppLayout></RequireAuth>} />
            <Route path="/assistant" element={<RequireAuth><AppLayout><Assistant /></AppLayout></RequireAuth>} />
            <Route path="/questionnaires" element={<RequireAuth><AppLayout><QuestionnaireAssistant /></AppLayout></RequireAuth>} />
            <Route path="/subscription" element={<RequireAuth><AppLayout><Subscription /></AppLayout></RequireAuth>} />
            <Route path="/admin" element={<AdminRedirect />} />

            {/* Unknown paths previously rendered a blank page (no catch-all) */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          )}
          </Suspense>
        </Router>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
